/**
 * BasicsStep Component
 * Step 1: Program basic information
 */

import React, { useMemo } from 'react';
import { FaInfoCircle } from 'react-icons/fa';
import ProgramImageUpload from '../components/ProgramImageUpload';
import { GAME_FORMATS, REGISTRATION_STATUSES } from '../constants/gameFormats';
import { useSports } from '../../../hooks/useSports';
import styles from './StepStyles.module.css';

const BasicsStep = ({
  formData,
  errors,
  updateField,
  categories,
  categoriesLoading,
  gameFormats,
  clubId
}) => {
  // Use dynamic sports from API
  const { sports, sportNames, getEmoji, getGameFormats: getSportGameFormats } = useSports();
  
  // Get game formats for selected sport
  const availableFormats = useMemo(() => {
    const sport = formData.sport;
    // Use API formats if available, otherwise fallback to constants
    return gameFormats[sport] || GAME_FORMATS[sport] || GAME_FORMATS.Other;
  }, [formData.sport, gameFormats]);

  // Reset game format when sport changes
  const handleSportChange = (e) => {
    const newSport = e.target.value;
    updateField('sport', newSport);
    
    // Reset game format if current one isn't valid for new sport
    const newFormats = gameFormats[newSport] || GAME_FORMATS[newSport] || GAME_FORMATS.Other;
    if (!newFormats.includes(formData.gameFormat)) {
      updateField('gameFormat', newFormats[0] || '');
    }
  };

  return (
    <div className={styles.stepContainer}>
      {/* Program Image */}
      <ProgramImageUpload
        value={formData.programImage}
        onChange={(image) => updateField('programImage', image)}
        clubId={clubId}
      />

      {/* Basic Info Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>
          <FaInfoCircle /> Basic Information
        </h3>

        {/* Program Name */}
        <div className={styles.formGroup}>
          <label className={`${styles.label} ${styles.required}`}>
            Program Name
          </label>
          <input
            type="text"
            className={`${styles.input} ${errors.name ? styles.error : ''}`}
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="e.g., Fall 2025 Recreational Soccer"
          />
          {errors.name && <span className={styles.errorText}>{errors.name}</span>}
        </div>

        {/* Category & Sport */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Category
            </label>
            <select
              className={`${styles.select} ${errors.category ? styles.error : ''}`}
              value={formData.category}
              onChange={(e) => updateField('category', e.target.value)}
              disabled={categoriesLoading}
            >
              {categoriesLoading ? (
                <option value="">Loading...</option>
              ) : categories.length === 0 ? (
                <option value="">No categories available</option>
              ) : (
                <>
                  <option value="">Select category</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>
                      {cat.name}
                    </option>
                  ))}
                </>
              )}
            </select>
            {errors.category && <span className={styles.errorText}>{errors.category}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Sport
            </label>
            <select
              className={`${styles.select} ${errors.sport ? styles.error : ''}`}
              value={formData.sport}
              onChange={handleSportChange}
            >
              {sportNames.map(sport => (
                <option key={sport} value={sport}>
                  {getEmoji(sport)} {sport}
                </option>
              ))}
            </select>
            {errors.sport && <span className={styles.errorText}>{errors.sport}</span>}
          </div>
        </div>

        {/* Game Format & Gender */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Game Format
            </label>
            <select
              className={`${styles.select} ${errors.gameFormat ? styles.error : ''}`}
              value={formData.gameFormat}
              onChange={(e) => updateField('gameFormat', e.target.value)}
            >
              <option value="">Select format</option>
              {availableFormats.map(format => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
            {errors.gameFormat && <span className={styles.errorText}>{errors.gameFormat}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Gender
            </label>
            <select
              className={styles.select}
              value={formData.gender}
              onChange={(e) => updateField('gender', e.target.value)}
            >
              <option value="Co-ed">Co-ed</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
        </div>

        {/* Age Range */}
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Minimum Age
            </label>
            <input
              type="number"
              className={`${styles.input} ${errors['age.min'] ? styles.error : ''}`}
              value={formData.age.min}
              onChange={(e) => updateField('age.min', e.target.value)}
              placeholder="e.g., 6"
              min="1"
              max="99"
            />
            {errors['age.min'] && <span className={styles.errorText}>{errors['age.min']}</span>}
          </div>

          <div className={styles.formGroup}>
            <label className={`${styles.label} ${styles.required}`}>
              Maximum Age
            </label>
            <input
              type="number"
              className={`${styles.input} ${errors['age.max'] ? styles.error : ''}`}
              value={formData.age.max}
              onChange={(e) => updateField('age.max', e.target.value)}
              placeholder="e.g., 12"
              min="1"
              max="99"
            />
            {errors['age.max'] && <span className={styles.errorText}>{errors['age.max']}</span>}
          </div>
        </div>
      </div>

      {/* Registration Status */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Registration Status</h3>
        
        <div className={styles.statusSelector}>
          {REGISTRATION_STATUSES.map(status => (
            <div
              key={status.value}
              className={`${styles.statusOption} ${formData.registrationStatus === status.value ? styles.active : ''}`}
              style={{ '--status-color': status.color }}
              onClick={() => updateField('registrationStatus', status.value)}
            >
              <span className={styles.statusIcon}>{status.icon}</span>
              <span className={styles.statusLabel}>{status.label}</span>
            </div>
          ))}
        </div>
        <p className={styles.hint}>
          Choose when participants can register for this program
        </p>
      </div>
    </div>
  );
};

export default BasicsStep;









